/**
 * Google Drive Picker (drive.file).
 * Folder picker for export destination; file picker for link-from-Drive.
 */

export type PickedDriveFolder = { id: string; name: string };

export type PickedDriveFile = {
  id: string;
  name: string;
  mimeType?: string;
};

type PickerTokenPayload = {
  accessToken: string;
  apiKey: string;
  clientId: string;
  /** Google Cloud project number — required for drive.file grants via Picker */
  appId?: string;
};

type GooglePickerBuilder = {
  addView: (view: unknown) => GooglePickerBuilder;
  setOAuthToken: (token: string) => GooglePickerBuilder;
  setDeveloperKey: (key: string) => GooglePickerBuilder;
  setAppId?: (appId: string) => GooglePickerBuilder;
  setCallback: (cb: (data: GooglePickerResponse) => void) => GooglePickerBuilder;
  setTitle?: (title: string) => GooglePickerBuilder;
  build: () => { setVisible: (v: boolean) => void };
};

type GooglePickerResponse = {
  action: string;
  docs?: Array<{ id?: string; name?: string; mimeType?: string }>;
};

declare global {
  interface Window {
    gapi?: {
      load: (api: string, cb: () => void) => void;
    };
    google?: {
      picker?: {
        Action: { PICKED: string; CANCEL: string };
        ViewId: { FOLDERS: string; DOCS: string };
        DocsView: new (viewId?: string) => {
          setIncludeFolders: (v: boolean) => unknown;
          setSelectFolderEnabled: (v: boolean) => unknown;
          setMimeTypes?: (mimes: string) => unknown;
        };
        PickerBuilder: new () => GooglePickerBuilder;
      };
    };
  }
}

const PROJECT_DOC_PICKER_MIME_TYPES =
  'application/pdf,image/jpeg,image/jpg,image/png,image/webp,image/gif';

let pickerLoadPromise: Promise<void> | null = null;

function loadPickerApi(): Promise<void> {
  if (window.google?.picker) return Promise.resolve();
  if (pickerLoadPromise) return pickerLoadPromise;

  pickerLoadPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>('script[data-leadsme-gapi]');
    const start = () => {
      if (!window.gapi?.load) {
        reject(new Error('Google API failed to load'));
        return;
      }
      window.gapi.load('picker', () => {
        if (!window.google?.picker) {
          reject(new Error('Google Picker failed to load'));
          return;
        }
        resolve();
      });
    };

    if (existing) {
      if (window.gapi) start();
      else existing.addEventListener('load', start);
      existing.addEventListener('error', () => reject(new Error('Google API script error')));
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://apis.google.com/js/api.js';
    script.async = true;
    script.defer = true;
    script.dataset.leadsmeGapi = '1';
    script.onload = start;
    script.onerror = () => reject(new Error('Google API script error'));
    document.head.appendChild(script);
  });

  return pickerLoadPromise;
}

/** Open a folder-only Google Picker. Resolves null if cancelled. */
export async function pickDriveFolder(
  token: PickerTokenPayload,
  title?: string
): Promise<PickedDriveFolder | null> {
  await loadPickerApi();
  const pickerNs = window.google?.picker;
  if (!pickerNs) throw new Error('Google Picker unavailable');

  return new Promise((resolve) => {
    const view = new pickerNs.DocsView(pickerNs.ViewId.FOLDERS);
    view.setIncludeFolders(true);
    view.setSelectFolderEnabled(true);
    if (typeof view.setMimeTypes === 'function') {
      view.setMimeTypes('application/vnd.google-apps.folder');
    }

    // App ID = Cloud project *number*. Required for drive.file to receive access on pick.
    let builder = new pickerNs.PickerBuilder()
      .addView(view)
      .setOAuthToken(token.accessToken)
      .setDeveloperKey(token.apiKey)
      .setCallback((data: GooglePickerResponse) => {
        if (data.action === pickerNs.Action.CANCEL) {
          resolve(null);
          return;
        }
        if (data.action !== pickerNs.Action.PICKED) return;
        const doc = data.docs?.[0];
        if (!doc?.id) {
          resolve(null);
          return;
        }
        resolve({ id: doc.id, name: doc.name || 'Folder' });
      });

    if (token.appId && typeof builder.setAppId === 'function') {
      builder = builder.setAppId(token.appId);
    }

    if (title && typeof builder.setTitle === 'function') {
      builder = builder.setTitle(title);
    }

    builder.build().setVisible(true);
  });
}

/** Open a file Google Picker (PDF / images). Resolves null if cancelled. */
export async function pickDriveFile(
  token: PickerTokenPayload,
  title?: string
): Promise<PickedDriveFile | null> {
  await loadPickerApi();
  const pickerNs = window.google?.picker;
  if (!pickerNs) throw new Error('Google Picker unavailable');

  return new Promise((resolve) => {
    const view = new pickerNs.DocsView(pickerNs.ViewId.DOCS);
    view.setIncludeFolders(true);
    view.setSelectFolderEnabled(false);
    if (typeof view.setMimeTypes === 'function') {
      view.setMimeTypes(PROJECT_DOC_PICKER_MIME_TYPES);
    }

    let builder = new pickerNs.PickerBuilder()
      .addView(view)
      .setOAuthToken(token.accessToken)
      .setDeveloperKey(token.apiKey)
      .setCallback((data: GooglePickerResponse) => {
        if (data.action === pickerNs.Action.CANCEL) {
          resolve(null);
          return;
        }
        if (data.action !== pickerNs.Action.PICKED) return;
        const doc = data.docs?.[0];
        if (!doc?.id) {
          resolve(null);
          return;
        }
        resolve({
          id: doc.id,
          name: doc.name || 'File',
          mimeType: doc.mimeType,
        });
      });

    if (token.appId && typeof builder.setAppId === 'function') {
      builder = builder.setAppId(token.appId);
    }

    if (title && typeof builder.setTitle === 'function') {
      builder = builder.setTitle(title);
    }

    builder.build().setVisible(true);
  });
}
